import React, { useMemo, useState } from "react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { PORTFOLIO_CATEGORIES, PORTFOLIO_GRID } from "@/data/content";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const filtered = useMemo(
    () => filter === "All" ? PORTFOLIO_GRID : PORTFOLIO_GRID.filter((p) => p.category === filter),
    [filter]
  );

  return (
    <div data-testid="page-portfolio" className="bg-[#FAF6F1]">
      
      <SEO title="Portfolio — Luxury Weddings, Corporate, Decor & Devotional Events" description="Selected stories from our studio: weddings, destination weddings, corporate events, social celebrations, devotional events, tablescapes, stage designs and artist nights." path="/portfolio" />
<section className="pt-28 md:pt-32 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Portfolio</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D] max-w-4xl">
            Stories we've had the <span className="italic text-[#B65980]">privilege</span> to design.
          </h1>
        </div>
      </section>

      <section className="py-6 bg-[#F4DCD8]/35 border-y border-[rgba(59,49,45,0.06)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-2 overflow-x-auto no-scrollbar">
          {["All", ...PORTFOLIO_CATEGORIES.map((c) => c.name)].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-[12px] tracking-[0.14em] uppercase border transition font-semibold ${
                filter === c
                  ? "bg-[#3B312D] text-white border-[#3B312D]"
                  : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.12)] hover:border-[#B65980]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p, i) => {
            const originalIndex = PORTFOLIO_GRID.indexOf(p);
            return (
            <Reveal key={p.title + i} delay={i * 40}
              data-testid={`portfolio-item-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative group overflow-hidden rounded-[20px] image-zoom h-[340px] md:h-[380px]">
              <Img imageKey={slotKeyFor.portfolio(p, originalIndex)} defaultSrc={p.image} defaultAlt={`${p.title} — ${p.category}`} className="absolute inset-0 w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B312D]/90 via-[#3B312D]/20 to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="pill-rose !bg-white/90 !border-white/0 !text-[#B65980]">{p.category}</span>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <p className="text-[10px] tracking-[0.24em] uppercase text-[#F4DCD8] font-semibold">{p.location}</p>
                <h3 className="font-display text-2xl md:text-3xl mt-1.5 leading-tight">{p.title}</h3>
              </div>
            </Reveal>
            );
          })}
        </div>
      </section>

      <BrochureCTA
        heading="Want to see how we plan complete experiences"
        text="Download our event deck for a closer look at services, planning layers, and celebration formats — from weddings to devotional events."
      />
      <FinalCTA />
    </div>
  );
}
